import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FLEET_FALLBACK_IMAGES } from '../constants/fleet';
import { getResponsiveImageUrl, createImagePlaceholder } from '../lib/imageOptimization';

/**
 * Optimized FleetImage component with preloading, lazy loading, and progressive loading
 */
const FleetImageOptimized = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = null,
  vehicleType = null,
  vehicleName = null,
  priority = false, // For critical images that should load immediately
  ...props 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // Priority images load immediately
  const [currentSrc, setCurrentSrc] = useState(src || '');
  const containerRef = useRef(null);
  const imageElRef = useRef(null);
  const observerRef = useRef(null);

  const computedFallback = useMemo(() => {
    if (fallbackSrc) return fallbackSrc;
    return (
      FLEET_FALLBACK_IMAGES[vehicleName] ||
      FLEET_FALLBACK_IMAGES[vehicleType] ||
      FLEET_FALLBACK_IMAGES['Sedan']
    );
  }, [fallbackSrc, vehicleName, vehicleType]);

  // Keep in sync when parent/DB image_url changes
  useEffect(() => {
    setCurrentSrc(src || '');
    setHasError(false);
    setIsLoaded(false);
  }, [src]);

  // Preload critical images
  useEffect(() => {
    if (priority && src && !src.startsWith('data:')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      if (/^https?:\/\//i.test(src) && !src.includes(window.location.origin)) {
        link.crossOrigin = 'anonymous';
      }
      link.fetchPriority = 'high';
      document.head.appendChild(link);
      
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [priority, src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) return; // Skip lazy loading for priority images

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    observerRef.current = observer;

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [priority]);

  // Cached images may skip onLoad — mark loaded if already complete
  useEffect(() => {
    if (!isInView) return;
    const img = imageElRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [isInView, currentSrc]);

  const handleError = useCallback(() => {
    // Already swapped once — terminal failure: hide spinner
    if (hasError) {
      setIsLoaded(true);
      return;
    }
    // Single-swap to a different fallback URL
    if (computedFallback && currentSrc !== computedFallback) {
      setHasError(true);
      setCurrentSrc(computedFallback);
      setIsLoaded(false);
      return;
    }
    // No alternate fallback (primary === fallback or missing) — stop spinner
    setHasError(true);
    setIsLoaded(true);
  }, [hasError, computedFallback, currentSrc]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const optimizedSrc = useMemo(() => {
    return getResponsiveImageUrl(currentSrc);
  }, [currentSrc]);

  const placeholder = useMemo(() => {
    return createImagePlaceholder(optimizedSrc);
  }, [optimizedSrc]);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      style={{ minHeight: '200px' }}
    >
      {!isLoaded && isInView && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse flex items-center justify-center">
          <img
            src={placeholder}
            alt=""
            className="w-full h-full object-cover opacity-30"
            style={{ filter: 'blur(5px)' }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      
      {isInView && (
        <img
          ref={imageElRef}
          src={optimizedSrc}
          alt={alt}
          className={`transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          onError={handleError}
          onLoad={handleLoad}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          {...props}
        />
      )}
    </div>
  );
};

export default FleetImageOptimized;
